import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useSubdomain } from '../contexts/SubdomainContext';
import { Building2 } from 'lucide-react';
import { resolveFileUrl } from '@/lib/utils';
import TradosPublicHeader from './TradosPublicHeader';
import WPChrome from './WPChrome';

// Default brand colors (fallback)
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';
const BRAND_YELLOW = '#FFD166';

const TRADOS_LOGO = 'https://www.trados.co/wp-content/uploads/2026/03/Trados-Trade-Verified.png';

// Default Trados logo component (used when no logo_url is configured)
const TradosLogo = ({ size = 'large' }) => {
  const height = size === 'large' ? 'h-10' : 'h-8';
  return <img src={TRADOS_LOGO} alt="Trados" className={`${height} w-auto object-contain`} />;
};

// Header component for public pages
export const PublicHeader = ({ transparent = false }) => {
  const { company, isSubdomain } = useSubdomain();
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Main domain: WordPress marketing header snapshot (Shadow DOM isolated),
  // falling back to the React header if the snapshot is unavailable.
  if (!isSubdomain) {
    return <WPChrome type="header" fallback={<TradosPublicHeader sticky={false} />} />;
  }

  // Subdomain: branded header for the company
  const headerBgColor = company?.theme_settings?.external_header_bg || '#FFFFFF';
  const headerTextColor = company?.theme_settings?.external_header_text || '#1F2937';
  const primaryColor = company?.theme_settings?.primary_color || BRAND_BLUE;
  const companyName = company?.name || 'Company';

  const getLogoUrl = (url) => {
    if (!url) return null;
    return resolveFileUrl(url, API_URL);
  };

  return (
    <nav
      className={`${transparent ? 'absolute top-0 left-0 right-0 z-50' : 'border-b border-gray-100'} py-4 px-6`}
      style={{ backgroundColor: transparent ? 'transparent' : headerBgColor }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          {company?.logo_url ? (
            <img
              src={getLogoUrl(company.logo_url)}
              alt={companyName}
              className="h-10 w-auto object-contain"
            />
          ) : (
            <>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-semibold" style={{ color: headerTextColor }}>{companyName}</span>
            </>
          )}
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="nav-login-button"
            className="px-5 py-2.5 rounded-xl font-semibold text-sm transition shadow-sm hover:shadow-md hover:-translate-y-0.5"
            style={{ backgroundColor: '#F5A623', color: '#1E2A35' }}
          >
            Login
          </Link>
          <Link
            to="/register"
            data-testid="nav-create-account-button"
            className="px-5 py-2.5 rounded-xl font-semibold text-sm transition shadow-sm hover:shadow-md hover:-translate-y-0.5 border"
            style={{ color: primaryColor, borderColor: primaryColor, backgroundColor: '#FFFFFF' }}
          >
            Create account
          </Link>
        </div>
      </div>
    </nav>
  );
};

// Footer component for public pages
export const PublicFooter = ({ dark = false }) => {
  const currentYear = new Date().getFullYear();
  const [platformTheme, setPlatformTheme] = useState(null);
  const { company, isSubdomain } = useSubdomain();
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    // Fetch platform public theme for main domain pages
    if (!isSubdomain) {
      const fetchPlatformTheme = async () => {
        try {
          const res = await api.get('/settings/public-theme');
          setPlatformTheme(res.data);
        } catch (error) {
          console.log('Using default theme');
        }
      };
      fetchPlatformTheme();
    }
  }, [isSubdomain]);

  // Get colors from company theme (for subdomain) or platform theme (for main domain)
  const footerBgColor = isSubdomain 
    ? (company?.theme_settings?.external_footer_bg || '#FFFFFF')
    : (platformTheme?.footer_bg || (dark ? '#111827' : BRAND_BLUE));
  const footerTextColor = isSubdomain 
    ? (company?.theme_settings?.external_footer_text || '#6B7280')
    : (platformTheme?.footer_text || '#FFFFFF');
  const primaryColor = isSubdomain 
    ? (company?.theme_settings?.primary_color || BRAND_BLUE)
    : (platformTheme?.primary_color || BRAND_BLUE);

  const logoUrl = isSubdomain 
    ? company?.logo_url 
    : platformTheme?.logo_url;
  const companyName = isSubdomain ? company?.name : null;

  const getLogoUrl = (url) => {
    if (!url) return null;
    return resolveFileUrl(url, API_URL);
  };

  // Main domain: WordPress marketing footer snapshot (Shadow DOM isolated).
  // Tenants keep the branded footer below.
  if (!isSubdomain) {
    return (
      <WPChrome
        type="footer"
        fallback={
          <footer className="py-8 px-6 border-t border-gray-100 text-center text-sm text-gray-500">
            &copy; {currentYear} {platformTheme?.site_title || 'Trados — The Trusted Trade Platform'}
          </footer>
        }
      />
    );
  }

  return (
    <footer
      className={`py-8 px-6 ${!isSubdomain && dark ? 'border-t border-gray-800' : 'border-t border-gray-100'}`}
      style={{ backgroundColor: footerBgColor }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Logo - smaller in footer */}
          <div className="flex items-center gap-2">
            {isSubdomain && company?.logo_url ? (
              <img 
                src={getLogoUrl(company.logo_url)}
                alt={companyName}
                className="h-8 w-auto object-contain"
              />
            ) : logoUrl ? (
              <img 
                src={getLogoUrl(logoUrl)}
                alt="Logo"
                className="h-10 w-auto object-contain"
              />
            ) : isSubdomain ? (
              <>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold" style={{ color: footerTextColor }}>
                  {companyName}
                </span>
              </>
            ) : (
              <TradosLogo size="small" textColor={footerTextColor} />
            )}
          </div>

          {/* Links */}
          <div className="flex items-center gap-6 text-sm" style={{ color: footerTextColor }}>
            <Link to="/" className="hover:opacity-80 transition">Home</Link>
            <Link to="/login" className="hover:opacity-80 transition">Login</Link>
            <Link to="/register" className="hover:opacity-80 transition">Register</Link>
          </div>

          {/* Copyright */}
          <div className="flex items-center gap-4 text-sm" style={{ color: footerTextColor }}>
            <p>
              &copy; {currentYear} {isSubdomain ? companyName : (platformTheme?.site_title || 'Trados — The Trusted Trade Platform')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Full page wrapper for public pages
const PublicLayout = ({ children, transparentHeader = false, darkFooter = false }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader transparent={transparentHeader} />
      <main className="flex-1">
        {children}
      </main>
      <PublicFooter dark={darkFooter} />
    </div>
  );
};

export default PublicLayout;
